import React, { useMemo, useState } from 'react';
import SectionHeader from './SectionHeader';
import Card from './Card';
import Button from './Button';

const projects = [
  {
    title: 'Intentioned',
    subtitle: 'intentioned.tech',
    link: 'https://intentioned.tech',
    tags: ['React', 'UI', 'Training', 'SaaS'],
    description:
      'A real-time voice-coaching app for practicing tough conversations out loud. Speech-to-text, an LLM playing the other side of the conversation, and text-to-speech all run locally on your own GPU through one FastAPI backend, which also scores the session afterward on things like clarity and tone.',
  },
  {
    title: "Where's My Rig",
    subtitle: 'wheresmyrig.com',
    link: 'https://wheresmyrig.com',
    tags: ['React', 'Cloudflare', 'Game', 'Leaderboard'],
    description:
      '[ALPHA] A GeoGuessr-style game for truck simulator fans. Guess real in-game locations from Euro Truck Simulator 2 and American Truck Simulator screenshots. Built with React, Cloudflare Workers, D1, and R2 with community screenshot submissions and a competitive leaderboard.',
  },
  {
    title: 'TidalHudson',
    subtitle: 'UN Millennium Fellowship',
    link: null,
    tags: ['VR', '3D', 'Education', 'Environment'],
    description:
      'Engineered a 3D VR visualization demonstrating the exact mechanics of an estuary system. Built as a contribution to the UN Millennium Fellowship, so people can see how the system actually works instead of reading a report about it.',
  },
  {
    title: 'truckvr',
    subtitle: 'TruckersMP VR for Linux',
    link: null,
    tags: ['VR', 'Game', 'Linux', 'Open Source'],
    description:
      'Get Euro Truck Simulator 2 and American Truck Simulator running in VR on Linux, streaming to a standalone headset with WiVRn, with or without TruckersMP. One script, both games, no SteamVR.',
  },
  {
    title: 'ProjectedMaps',
    subtitle: 'The projection follows the task',
    link: 'https://kojesko.github.io/ProjectedMaps/projection-switching-prototype.html',
    tags: ['D3', 'Maps', 'Cartography', 'Data Viz'],
    description:
      '[IN PROGRESS] A navigation-layer demo for the UN "Correct the Map" resolution. Four map tasks, each drawn in the projection that suits it (Equal Earth, Lambert conic, azimuthal equidistant, gnomonic), with angle and area distortion measured live from each projection\'s Jacobian instead of asserted. One self-contained HTML file, no server.',
  },
];

function Projects() {
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('All');

  const allTags = useMemo(() => {
    const tagSet = new Set();
    projects.forEach((project) => {
      project.tags.forEach((tag) => tagSet.add(tag));
    });
    return ['All', ...Array.from(tagSet)];
  }, []);

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return projects.filter((project) => {
      const matchesTag = activeTag === 'All' || project.tags.includes(activeTag);
      const matchesText =
        normalizedQuery.length === 0 ||
        project.title.toLowerCase().includes(normalizedQuery) ||
        project.subtitle.toLowerCase().includes(normalizedQuery) ||
        project.description.toLowerCase().includes(normalizedQuery) ||
        project.tags.some((tag) => tag.toLowerCase().includes(normalizedQuery));

      return matchesTag && matchesText;
    });
  }, [activeTag, query]);

  return (
    <section id="projects" className="section projects">
      <div className="container">
        <SectionHeader title="The Work" subtitle="Projects" />
        <div className="filter-toolbar" aria-label="Project filters">
          <input
            className="filter-input"
            type="search"
            placeholder="Search projects by title or technology"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="tag-row">
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`tag-chip ${activeTag === tag ? 'active' : ''}`}
                onClick={() => setActiveTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        <div className="projects-grid">
          {filteredProjects.map((project) => (
            <Card
              key={project.title}
              title={project.title}
              subtitle={project.subtitle}
              className="interactive-card project-card"
            >
              <p>{project.description}</p>
              <div className="skill-tag-list" aria-label="Project tags">
                {project.tags.map((tag) => (
                  <span key={`${project.title}-${tag}`} className="mini-tag">
                    {tag}
                  </span>
                ))}
              </div>
              {project.link && (
                <Button href={project.link} variant="outline">
                  Visit Site
                </Button>
              )}
              <p className="hover-note">Hover card for lift effect</p>
            </Card>
          ))}
        </div>
        {filteredProjects.length === 0 && (
          <p className="filter-empty">No projects match your current filter.</p>
        )}
      </div>
    </section>
  );
}

export default Projects;
